
import { useState } from "react";
import { MapPin, Phone, Mail, ChevronLeft, ChevronRight } from "lucide-react";

// Numbers stored as offset-encoded char arrays — never appear as plain strings
const decode = (arr) => arr.map((n) => String.fromCharCode(n - 3)).join("");
const PHONES = [
  { raw: [46,54,56,52,35,60,57,56,55,59,51,59,51,60], display: "+351 965 480 809" },
  { raw: [46,54,56,52,35,60,57,51,55,59,58,56,52,55], display: "+351 960 487 514" },
];

function ProtectedPhone({ phone }) {
  return (
    <a href={`tel:${decode(phone.raw)}`} className="hover:text-yellow-600 transition-colors">
      {phone.display}
    </a>
  );
}

const galleryImages = [
  ...Array.from({ length: 35 }, (_, i) => `/images/web/${i + 1}.webp`),
  "/images/web/36a.webp",
  ...Array.from({ length: 6 }, (_, i) => `/images/web/${i + 37}.webp`),
];

const BOOKING_URL = {
  en: "https://www.booking.com/hotel/pt/casa-da-porta-amarela-ponta-delgada.en-gb.html",
  pt: "https://www.booking.com/hotel/pt/casa-da-porta-amarela-ponta-delgada.pt-pt.html",
};

const AIRBNB_URL = "https://www.airbnb.pt/rooms/1684306962147183950";

const content = {
  en: {
    title: "Casa da Porta Amarela",
    subtitle: "Your home in the heart of the Azores",
    description:
      "This charming villa reflects the authenticity of the Azores, combining the charm of the past with contemporary comfort. Fully renovated, it preserves original elements that give it a unique character. With 3 bedrooms and 6 beds across 180 m², it is ideal for couples seeking tranquility or groups that value comfort — centrally located in Ponta Delgada, close to everything.",
    gallery: "Our Space",
    book: "Book Your Stay",
    bookCta: "Reserve on Booking.com",
    bookAirbnb: "Reserve on Airbnb",
    bookNote: "Best rates guaranteed · Free cancellation available",
    bookPerks: [
      "Entire house · 180 m²",
      "3 bedrooms · 6 beds",
      "Terrace & BBQ · Balcony",
      "Equipped kitchen",
      "Living room",
      "Washing machine & dishwasher",
      "Air conditioning & heating",
      "Free Wi-Fi",
      "Flat-screen TV",
      "Pets welcome on request",
    ],
    checkIn: "Check-in: 16:00 – 23:00",
    checkOut: "Check-out: 08:00 – 11:00",
    contact: "Find Us",
    address: "Rua do Passal, n48, Ponta Delgada, Azores",
    email: "casadaportaamarela48@gmail.com",
    map: "Where we are",
    counter: (i, total) => `${i + 1} / ${total}`,
  },
  pt: {
    title: "Casa da Porta Amarela",
    subtitle: "A sua casa no coração dos Açores",
    description:
      "Esta encantadora moradia reflecte a autenticidade açoriana, combinando o charme do passado com o conforto contemporâneo. Totalmente renovada, preserva elementos originais que lhe conferem um carácter único. Com 3 quartos e 6 camas distribuídos por 180 m², é ideal para casais em busca de tranquilidade ou grupos que valorizam o conforto — com localização central em Ponta Delgada, perto de tudo.",
    gallery: "O Nosso Espaço",
    book: "Reserve a Sua Estadia",
    bookCta: "Reservar no Booking.com",
    bookAirbnb: "Reservar no Airbnb",
    bookNote: "Melhores preços garantidos · Cancelamento gratuito disponível",
    bookPerks: [
      "Casa inteira · 180 m²",
      "3 quartos · 6 camas",
      "Terraço & churrascos · Varanda",
      "Cozinha equipada",
      "Sala de estar",
      "Máquina de lavar e lavar louça",
      "Ar condicionado & aquecimento",
      "Wi-Fi gratuito",
      "Televisão de ecrã plano",
      "Animais admitidos mediante pedido",
    ],
    checkIn: "Check-in: 16:00 – 23:00",
    checkOut: "Check-out: 08:00 – 11:00",
    contact: "Encontre-nos",
    address: "Rua do Passal, n48, Ponta Delgada, Açores",
    email: "casadaportaamarela48@gmail.com",
    map: "Onde estamos",
    counter: (i, total) => `${i + 1} / ${total}`,
  },
};

export default function CasaPortaAmarela() {
  const [lang, setLang] = useState("en");
  const [slide, setSlide] = useState(41);
  const t = content[lang];

  const prev = () => setSlide((i) => (i - 1 + galleryImages.length) % galleryImages.length);
  const next = () => setSlide((i) => (i + 1) % galleryImages.length);

  return (
    <div className="min-h-screen bg-amber-50 text-stone-800 font-serif">

      {/* Header */}
      <header className="bg-white border-b-2 border-yellow-300 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto flex justify-between items-center px-6 py-3">
          <a href="#home" className="flex items-center space-x-4 group">
            <img
              src="/logo.png"
              alt="Casa da Porta Amarela"
              className="h-16 w-auto object-contain"
            />
            <div>
              <h1 className="text-xl font-bold tracking-tight leading-tight text-stone-800 group-hover:text-yellow-600 transition-colors">
                Casa da Porta Amarela
              </h1>
              <p className="text-xs text-stone-500 tracking-widest uppercase">Ponta Delgada · Açores</p>
            </div>
          </a>
          <nav className="flex items-center space-x-7 text-sm font-medium">
            <a href="#home" className="text-stone-600 hover:text-yellow-600 transition-colors">Home</a>
            <a href="#gallery" className="text-stone-600 hover:text-yellow-600 transition-colors">{t.gallery}</a>
            <a href="#book" className="text-stone-600 hover:text-yellow-600 transition-colors">{t.book}</a>
            <a href="#contact" className="text-stone-600 hover:text-yellow-600 transition-colors">{t.contact}</a>
            <div className="flex space-x-2 pl-2 border-l border-stone-200">
              <button onClick={() => setLang("en")} className={`rounded overflow-hidden transition-opacity ${lang === "en" ? "opacity-100 ring-2 ring-yellow-400" : "opacity-50 hover:opacity-80"}`}>
                <img src="https://flagcdn.com/w40/gb.png" alt="English" className="w-7 h-5 block" />
              </button>
              <button onClick={() => setLang("pt")} className={`rounded overflow-hidden transition-opacity ${lang === "pt" ? "opacity-100 ring-2 ring-yellow-400" : "opacity-50 hover:opacity-80"}`}>
                <img src="https://flagcdn.com/w40/pt.png" alt="Português" className="w-7 h-5 block" />
              </button>
            </div>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-16">

        {/* Hero */}
        <section id="home" className="text-center space-y-4">
          <div className="flex justify-center mb-2">
            <span className="inline-block w-12 h-1 rounded bg-yellow-400" />
          </div>
          <h2 className="text-5xl font-bold text-stone-800 tracking-tight">{t.title}</h2>
          <p className="text-lg italic text-yellow-700">{t.subtitle}</p>
          <p className="text-base text-stone-500 max-w-2xl mx-auto leading-relaxed">{t.description}</p>
        </section>

        {/* Gallery slideshow */}
        <section id="gallery">
          <h3 className="text-2xl font-semibold mb-6 text-stone-700 flex items-center gap-3">
            <span className="inline-block w-6 h-0.5 bg-yellow-400" />
            {t.gallery}
          </h3>
          <div className="relative rounded-2xl overflow-hidden shadow-lg bg-stone-900 select-none">
            <img
              key={slide}
              src={galleryImages[slide]}
              alt={`Casa da Porta Amarela ${slide + 1}`}
              className="w-full object-cover max-h-[600px]"
            />
            {/* Arrows */}
            <button
              onClick={prev}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-yellow-400 text-stone-800 rounded-full p-2 shadow transition-colors"
              aria-label="Previous"
            >
              <ChevronLeft size={28} />
            </button>
            <button
              onClick={next}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-yellow-400 text-stone-800 rounded-full p-2 shadow transition-colors"
              aria-label="Next"
            >
              <ChevronRight size={28} />
            </button>
            {/* Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white text-sm px-3 py-1 rounded-full tracking-wide">
              {t.counter(slide, galleryImages.length)}
            </div>
          </div>
          {/* Dot strip */}
          <div className="flex justify-center gap-1.5 mt-4 flex-wrap">
            {galleryImages.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                className={`w-2 h-2 rounded-full transition-colors ${i === slide ? "bg-yellow-500" : "bg-stone-300 hover:bg-yellow-300"}`}
                aria-label={`Go to photo ${i + 1}`}
              />
            ))}
          </div>
        </section>

        {/* Booking */}
        <section id="book">
          <h3 className="text-2xl font-semibold mb-6 text-stone-700 flex items-center gap-3">
            <span className="inline-block w-6 h-0.5 bg-yellow-400" />
            {t.book}
          </h3>
          <div className="bg-white rounded-2xl shadow-sm border border-yellow-100 overflow-hidden">
            <div className="bg-yellow-400 px-6 py-4 flex items-center gap-3">
              <img src="/logo.png" alt="" className="h-10 w-auto" />
              <div>
                <p className="font-bold text-stone-800 text-lg leading-tight">Casa da Porta Amarela</p>
                <p className="text-stone-700 text-sm">Ponta Delgada · Açores</p>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <ul className="grid grid-cols-2 gap-3">
                {t.bookPerks.map((perk) => (
                  <li key={perk} className="flex items-center gap-2 text-stone-600 text-sm">
                    <span className="w-2 h-2 rounded-full bg-yellow-400 shrink-0" />
                    {perk}
                  </li>
                ))}
              </ul>
              <div className="flex justify-center gap-6 text-sm text-stone-500 border-t border-stone-100 pt-4">
                <span>🕓 {t.checkIn}</span>
                <span>🕙 {t.checkOut}</span>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="flex flex-wrap justify-center gap-3">
                  <a
                    href={BOOKING_URL[lang]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#003580] hover:bg-[#00276b] text-white font-bold px-6 py-3 rounded-full shadow transition-colors text-sm"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
                      <path d="M17.3 3H6.7A3.7 3.7 0 0 0 3 6.7v10.6A3.7 3.7 0 0 0 6.7 21h10.6A3.7 3.7 0 0 0 21 17.3V6.7A3.7 3.7 0 0 0 17.3 3zm-5.1 13.4a4.4 4.4 0 1 1 0-8.8 4.4 4.4 0 0 1 0 8.8zm4.6-8a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
                    </svg>
                    {t.bookCta}
                  </a>
                  <a
                    href={AIRBNB_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#FF5A5F] hover:bg-[#e04e53] text-white font-bold px-6 py-3 rounded-full shadow transition-colors text-sm"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.24 0 2.25 1.01 2.25 2.25S13.24 9.5 12 9.5 9.75 8.49 9.75 7.25 10.76 5 12 5zm4.5 12.5c0 .28-.22.5-.5.5h-8c-.28 0-.5-.22-.5-.5v-.5c0-2.21 1.79-4 4-4s4 1.79 4 4v.5z"/>
                    </svg>
                    {t.bookAirbnb}
                  </a>
                </div>
                <p className="text-xs text-stone-400">{t.bookNote}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact">
          <h3 className="text-2xl font-semibold mb-6 text-stone-700 flex items-center gap-3">
            <span className="inline-block w-6 h-0.5 bg-yellow-400" />
            {t.contact}
          </h3>
          <div className="bg-white rounded-2xl shadow-sm border border-yellow-100 p-6 space-y-4">
            <div className="flex items-start space-x-3">
              <MapPin className="text-yellow-500 mt-0.5 shrink-0" />
              <span className="text-stone-600">{t.address}</span>
            </div>
            {PHONES.map((ph) => (
              <div key={ph.display} className="flex items-center space-x-3">
                <Phone className="text-yellow-500 shrink-0" />
                <span className="text-stone-600"><ProtectedPhone phone={ph} /></span>
              </div>
            ))}
            <div className="flex items-center space-x-3">
              <Mail className="text-yellow-500 shrink-0" />
              <span className="text-stone-600">{t.email}</span>
            </div>
          </div>
        </section>

        {/* Map */}
        <section>
          <h3 className="text-2xl font-semibold mb-6 text-stone-700 flex items-center gap-3">
            <span className="inline-block w-6 h-0.5 bg-yellow-400" />
            {t.map}
          </h3>
          <div className="overflow-hidden rounded-2xl shadow-sm border border-yellow-100">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d243.38096108309197!2d-25.6660776!3d37.7450881!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDQ0JzQyLjMiTiAyNcKwMzknNTcuOSJX!5e0!3m2!1sen!2spt!4v1721080000000!5m2!1sen!2spt"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
            />
          </div>
        </section>
      </main>

      <footer className="bg-white border-t-2 border-yellow-300 py-6 text-center text-sm text-stone-400 mt-8">
        © {new Date().getFullYear()} Casa da Porta Amarela · Ponta Delgada, Açores
      </footer>
    </div>
  );
}
